import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  input,
  model,
  output,
  signal,
  viewChild
} from '@angular/core';
import {AppContext} from "../../../../app-context";
import moment from "moment";
import {
  IE3ContainerProcessSummary,
  IE3ContainerStatus,
  IE3RefreshContainerProcessSummary,
  IE3SizeType,
  TaskMessageStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import {ContainerUtils} from "../../container.utils";
import {CheckboxSelectionState, executeWhen, sendCommand} from "../../../../common/utils";
import {DeclarationMessageStatus} from "../../../visit-overview/visit-overview-item/visit-overview.utils";
import {PortvisitUtils} from "../../../../refdata/portvisit-utils";
import {ConsignmentUtils} from "../../../consignments/consignment.utils";
import {SentenceCasePipe} from "../../../../common/sentence-case.pipe";

@Component({
  selector: 'app-container-overview-item',
  templateUrl: './container-overview-item.component.html',
  styleUrls: ['./container-overview-item.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContainerOverviewItemComponent {
  utils = ContainerUtils;
  consignmentUtils = ConsignmentUtils;
  appContext = AppContext;

  readonly container = model.required<IE3ContainerProcessSummary>();
  readonly term = input<string>();
  readonly depth = input<number>();
  readonly collapsible = input<boolean>();
  readonly selected = input<boolean>(false);
  readonly selectionToggled = output<void>();
  readonly checkboxSelectionState = CheckboxSelectionState;

  readonly collapseButton = viewChild<ElementRef>("collapseButton");

  selectionWidth: number = 40;
  collapsed = signal(true);

  readonly dischargeStatus = computed(() => this.getDischargeStatus());
  readonly dischargeStatuses = computed(() => [this.dischargeStatus()]);
  readonly dischargeListStatus = computed(() => this.getDischargeListStatus());
  readonly isEmpty = computed(() => this.container().consignmentDataSummaries ? this.container().consignmentDataSummaries[0]?.empty : false);
  readonly sizeType = computed<IE3SizeType>(() => {
    const c = this.container();
    return c.consignmentDataSummaries
      ? c.consignmentDataSummaries[0]?.containerSizeAndType || c.terminalDataSummary?.sizeType
      : c.terminalDataSummary?.sizeType;
  });
  readonly containerOperator = computed(() => {
    const c = this.container();
    return c.consignmentDataSummaries
      ? c.consignmentDataSummaries[0]?.containerOperator || c.terminalDataSummary?.containerOperator
      : c.terminalDataSummary?.containerOperator;
  });
  readonly terminal = computed(() => SentenceCasePipe.format(
    this.container().terminalDataSummary?.terminal
    || this.container().dischargeListData?.terminalname
    || (this.container().consignmentDataSummaries?.length ? this.container().consignmentDataSummaries[0].dischargeTerminal?.terminalName : '')
  ));

  readonly selectable = computed(() => this.container().consignmentDataSummaries.length == 0);

  formatDate = (dateString: string): string => !!dateString ? moment(dateString).format("DD MMM HH:mm") : null;

  getDischargeStatus = (): DeclarationMessageStatus => {
    const container = this.container();
    return {
      name: "Discharge details",
      taskStatus: this.getDischargeTaskStatus(),
      messages: container.terminalDataSummary?.actualTimeOfHandling
        ? [`Discharged at: ${this.formatDate(container.terminalDataSummary?.actualTimeOfHandling)}`] : [],
      customLabel: this.getDischargeLabel()
    }
  }

  private getDischargeTaskStatus(): TaskMessageStatus {
    const container = this.container();
    switch (container.status) {
      case IE3ContainerStatus.DISCHARGED:
        return container.overlanded ? TaskMessageStatus.ERROR : TaskMessageStatus.ACCEPTED;
      case IE3ContainerStatus.EXPECTED:
        return TaskMessageStatus.UNKNOWN;
      case IE3ContainerStatus.SHORTLANDED:
        return TaskMessageStatus.ERROR;
      case IE3ContainerStatus.REMAINS_ON_BOARD:
        return TaskMessageStatus.UNKNOWN;
    }
  }

  getDischargeListStatus = (): DeclarationMessageStatus => {
    const container = this.container();
    if (container.dischargeListData != null) {
      return {
        name: "Discharge list details",
        taskStatus: TaskMessageStatus.ACCEPTED,
        messages: [`Sent in discharge list to terminal: ${container.dischargeListData.terminalname} - ${container.dischargeListData.terminalCode}`],
        customLabel: 'Listed'
      }
    }
    return null;
  }

  private getDischargeLabel(): string {
    const container = this.container();
    switch (container.status) {
      case IE3ContainerStatus.DISCHARGED:
        return container.overlanded ? "Overlanded" : "Discharged";
      default:
        return PortvisitUtils.enumFormatter(container.status);
    }
  }

  validSelector = (input: string): string => "SELECTOR_" + input.replace(/[^a-zA-Z0-9]/g, '');

  onHighlightMatch() {
    if (!this.collapsible()) {
      return;
    }
    this.collapsed.set(false);
    const id = this.validSelector(this.container().containerNumber);
    executeWhen(() => !!document.getElementById(id),
      () => {
        const el = document.getElementById(id);
        if (el) {
          el.classList.add('show');
          this.collapseButton()?.nativeElement?.setAttribute('aria-expanded', 'true');
        }
      }, 10);
  }

  refreshSummary = () =>
    sendCommand('com.portbase.bezoekschip.common.api.consignments.commands.RefreshContainerProcessSummary', <IE3RefreshContainerProcessSummary>{
      containerProcessId: this.container().containerProcessId
    }, (summary: IE3ContainerProcessSummary) => {
      this.container.set(summary.removed ? null : summary);
      AppContext.registerSuccess('Container summary has been rebuild successfully.');
    });
}
